import { useState, useEffect } from "react";
import api from "@/lib/api";
import { imgProxy } from "@/lib/img";
import { StarRating } from "@/components/dashboard/Reviews";
import ProfileDetailDialog from "@/components/dashboard/ProfileDetailDialog";
import { MessageSquareQuote, Loader2, ExternalLink, Globe } from "lucide-react";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1581197326868-51cafbd1a169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85";

function fmtWhen(c) {
  if (c.date_raw) return c.date_raw;
  if (c.date_iso) {
    try {
      return new Date(c.date_iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
    } catch (e) { /* noop */ }
  }
  return "";
}

export default function CommentsView({ category = "" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ profiles_with_reviews: 0 });
  const [sources, setSources] = useState([]);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/comments/recent", { params: { category, limit: 100 } })
      .then(({ data }) => { if (active) { setItems(data.items || []); setMeta(data); } })
      .catch(() => active && setItems([]))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [category]);

  useEffect(() => {
    api.get("/comments/sources").then(({ data }) => setSources(data.sources || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-4" data-testid="comments-view">
      {/* Header — explains the aggregator edge */}
      <div className="bg-[#18181b] border border-[#27272a] p-5 flex items-start gap-4">
        <div className="w-10 h-10 border border-[#c8aa6e] flex items-center justify-center shrink-0">
          <MessageSquareQuote className="w-4 h-4 text-[#c8aa6e]" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <h3 className="font-head text-lg text-[#fafafa]">Comentários dos sites, num só lugar</h3>
          <p className="text-sm text-[#71717a]">
            Reunimos as opiniões públicas deixadas nos vários sites de origem. Assim vê a reputação de cada perfil sem andar de site em site.
          </p>
          {sources.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-[#a1a1aa]">
              <Globe className="w-3.5 h-3.5 text-[#c8aa6e]" />
              <span>Fontes com comentários:</span>
              {sources.map((s) => (
                <span key={s.key} className="border border-[#3f3f46] px-2 py-0.5 text-[#e4e4e7]">{s.name}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#71717a] py-16 justify-center">
          <Loader2 className="w-4 h-4 spin" /> A carregar comentários…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-[#18181b] border border-[#27272a] px-4 py-12 text-center text-[#71717a] text-sm">
          Ainda não há comentários recolhidos. Vá a Admin → Recolha para recolher comentários das fontes.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((c, i) => (
            <div key={i} data-testid={`comment-${i}`} className="bg-[#18181b] border border-[#27272a] p-4 flex gap-3">
              <button
                type="button"
                onClick={() => setDetailId(c.profile_id)}
                className="shrink-0"
                title="Ver perfil"
              >
                <img
                  src={imgProxy(c.photo_url, 160)}
                  onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
                  alt={c.name}
                  loading="lazy"
                  decoding="async"
                  className="w-14 h-14 object-cover border border-[#27272a] bg-[#27272a]"
                />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setDetailId(c.profile_id)}
                    className="text-sm text-[#fafafa] hover:text-[#c8aa6e] truncate max-w-[10rem]"
                  >
                    {c.name || "—"}
                  </button>
                  <span className="text-[11px] text-[#71717a]">· {c.city || "—"}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#c8aa6e] border border-[#c8aa6e]/40 px-1.5 py-0.5 ml-auto">{c.source_name}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <StarRating value={c.rating} />
                  <span className="text-[11px] text-[#71717a] font-mono">{fmtWhen(c)}</span>
                </div>
                <p className="text-[13px] text-[#d4d4d8] leading-relaxed mt-1.5 whitespace-pre-line break-words">{c.text}</p>
                {c.profile_url && (
                  <a href={c.profile_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#c8aa6e] hover:text-[#d4b982] mt-1.5">
                    <ExternalLink className="w-3 h-3" /> Anúncio original
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ProfileDetailDialog profileId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
